package games.polyclub.website.frontend.store.pages

import adminlte.layout.Layout
import adminlte.layout.LayoutConfig
import adminlte.layout.LayoutOptions
import games.polyclub.frontend.lib.react.bootstrap.components.BAlert
import games.polyclub.frontend.lib.react.bootstrap.css.utility.BootstrapColor
import games.polyclub.frontend.lib.react.bootstrap.custom.components.ValidatingInput
import games.polyclub.frontend.lib.react.ga4.GA4
import games.polyclub.frontend.lib.react.hcaptcha.async.AsyncHCaptcha
import games.polyclub.frontend.lib.react.simplified.SFC
import games.polyclub.frontend.lib.react.simplified.tags.*
import games.polyclub.frontend.lib.react.utils.classSet
import games.polyclub.frontend.lib.react.utils.classes
import games.polyclub.frontend.store.shared.LoginRequest
import games.polyclub.service.config.api.SecretString.Companion.asSecret
import games.polyclub.website.frontend.store.StoreBff
import games.polyclub.website.frontend.store.appScope
import games.polyclub.website.frontend.store.components.Loading
import games.polyclub.website.frontend.store.components.MinecraftInput
import games.polyclub.website.frontend.store.context.useCart
import games.polyclub.website.frontend.store.hooks.GAAction
import games.polyclub.website.frontend.store.hooks.GACategory
import games.polyclub.website.frontend.store.hooks.logEvent
import games.polyclub.website.frontend.store.hooks.useAuth
import games.polyclub.website.frontend.store.sitemap.PublicSitemap
import jquery.jQuery
import js.core.jso
import kotlinx.coroutines.async
import kotlinx.coroutines.launch
import react.*
import react.dom.events.FormEventHandler
import react.router.dom.Link
import react.router.useLocation
import web.cssom.pct
import web.dom.document
import web.html.*
import web.prompts.alert

val LoginPage = SFC<Props> { _ ->
    useEffect {
        document.body.style.height = ""
        jQuery("#wrapper").addClass("login-box")
        jQuery("body").Layout(jso<LayoutOptions> {
            loginRegisterAutoHeight = true
        }).addClass("login-page")
            .Layout(LayoutConfig.INIT)
            .Layout(LayoutConfig.FIX_LOGIN_REGISTER_HEIGHT)
        cleanup {
            jQuery("body").removeClass("login-page")
            jQuery("#wrapper").removeClass("login-box")
        }
    }

    val location = useLocation()

    useEffectOnce {
        GA4.set(jso {
            this.page = location.pathname + "#!/login"
            this.title = "Login"
        })
        GA4.send(jso {
            this.hitType = "pageview"
            this.page = location.pathname + "#!/login"
            this.title = "Login"
        })
    }

    val cartCtx = useCart()
    val authCtx = useAuth()

    val (hCaptchaToken, setHCaptchaToken) = useState<String?>(null)
    val (userValid, setUserValid) = useState<Boolean?>(null)
    val (passValid, setPassValid) = useState<Boolean?>(null)
    val (readyToSubmit, setReadyToSubmit) = useState(false)
    val (persistent, setPersistent) = useState<Boolean?>(null)
    val (invalidCredentials, setInvalidCredentials) = useState(false)
    val (errorHappened, setErrorHappened) = useState(false)
    val (isSubmitting, setIsSubmitting) = useState(false)
    val (nick, setNick) = useState<String?>(cartCtx.selfIdentification?.name ?: "")
    val (passwd, setPasswd) = useState("")

    val asyncHCaptchaRef = useRef<AsyncHCaptcha>()
    val formRef = useRef<HTMLFormElement>()
    val passwordRef = useRef<ValidatingInput>()
    val rememberMeRef = useRef<HTMLInputElement>()
    val submitRef = useRef<HTMLButtonElement>()

    useEffect(passValid, userValid, hCaptchaToken) {
        console.log(passValid, userValid, hCaptchaToken)
        setReadyToSubmit(passValid == true && userValid == true && hCaptchaToken != null)
    }

    val createRequest = useCallback(nick, userValid, passwordRef, hCaptchaToken, rememberMeRef) {
        appScope.async r@{
            val user = nick?.takeIf { userValid == true }
            val password = passwordRef.current?.validValueAsync()
            val remember = rememberMeRef.current?.checked

            LoginRequest(
                user = user?.asSecret() ?: run { console.log("Bad user"); return@r null},
                password = password?.await()?.asSecret() ?: run { console.log("Bad email"); return@r null},
                captcha = hCaptchaToken ?: run { console.log("Bad hCaptcha"); return@r null},
                remember = remember ?: run { console.log("Bad remember"); return@r null},
            )
        }
    }

    val handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
        setInvalidCredentials,
        setErrorHappened,
        setIsSubmitting,
        setReadyToSubmit,
        passwordRef,
        createRequest,
        asyncHCaptchaRef,
        authCtx,
        formRef,
    ) { ev ->
        ev.preventDefault()
        setInvalidCredentials(false)
        setErrorHappened(false)
        setIsSubmitting(true)
        setReadyToSubmit(false)
        passwordRef.current?.isForcedValid = null
        appScope.launch {
            val request = createRequest().await()
            console.log("Request criado: ${request != null}")
            setReadyToSubmit(request != null)
            if (request == null) {
                logEvent(
                    category = GACategory.LOGIN,
                    action = GAAction.LOGIN_FAILED,
                    label = "Tentou fazer login mas a criação do request falhou"
                )
                return@launch
            }
            asyncHCaptchaRef.current?.hCaptchaRef?.current?.resetCaptcha()
            val response = try {
                StoreBff.login(request)
            } catch (e: Throwable) {
                console.log("Falha ao autenticar")
                logEvent(
                    category = GACategory.LOGIN,
                    action = GAAction.LOGIN_FAILED,
                    label = "Tentou fazer login mas ocorreu uma Exception"
                )
                e.printStackTrace()
                setErrorHappened(true)
                return@launch
            }
            if (response == null) {
                logEvent(
                    category = GACategory.LOGIN,
                    action = GAAction.LOGIN_FAILED,
                    label = "Tentou fazer login mas a resposta veio nula"
                )
                setInvalidCredentials(true)
                setReadyToSubmit(false)
                return@launch
            }
            if (response.isEmpty) {
                logEvent(
                    category = GACategory.LOGIN,
                    action = GAAction.LOGIN_FAILED,
                    label = "Tentou fazer login mas a resposta veio vazia"
                )
                setInvalidCredentials(true)
                setReadyToSubmit(false)
                return@launch
            }

            GA4.set(jso {
                this.userId = response.get().profile.id.toInt()
            })
            authCtx.setAuth(response.get())
            authCtx.setPersist(request.remember)
            formRef.current?.reset()
        }.invokeOnCompletion {
            setIsSubmitting(false)
            setReadyToSubmit(false)
        }
    }

    div("card card-outline card-primary") {
        div("card-header text-center") {
            Link("h1") {
                to = PublicSitemap.HOME.path
                img {
                    style = jso {
                        width = 100.pct
                    }
                    src = "img/logo.png"
                }
            }
        }
        div("card-body") {
            if (!isSubmitting) {
                classSet += "d-none"
            }
            Loading()
        }
        div("card-body") {
            if (isSubmitting) {
                classSet += "d-none"
            }
            when {
                invalidCredentials -> BAlert {
                    color = BootstrapColor.WARNING
                    +"Usuário ou senha inválidos ou sem cadastro, se você ainda não tem senha, por favor clique em \"Cadastrar uma senha\" ou se esqueceu em \"Esqueci minha senha\""
                }

                errorHappened -> BAlert {
                    color = BootstrapColor.DANGER
                    +"Ocorreu um erro ao processar o seu pedido de conexão!"
                }
            }
            p("login-box-msg") {
                +"Opa, precisamos autenticar você para prosseguir. Por favor, crie uma senha caso você ainda não tenha."
            }
            form {
                ref = formRef
                noValidate = true
                onSubmit = handleSubmit
                MinecraftInput("mb-3") {
                    placeholder = "Apelido no Minecraft"
                    required = true
                    value = nick
                    hideValidPrepend = true
                    iconAtRight = true
                    onChange = { nick ->
                        setNick(nick)
                    }
                    onValidationChange = { _, valid, _ ->
                        setUserValid(valid)
                    }
                }
                div("input-group mb-3") {
                    ValidatingInput::class {
                        classes = "form-control"
                        ref = passwordRef
                        type = InputType.password
                        placeholder = "Senha (do site, não é a do Minecraft!)"
                        required = true
                        value = passwd
                        if (invalidCredentials) {
                            isValid = false
                        }
                        if (errorHappened) {
                            isValid = true
                        }
                        onChange = {
                            setPasswd(it.target.value)
                        }
                        onValidationStateChange = { ev ->
                            setPassValid(ev.current)
                        }
                    }
                    div("input-group-append") {
                        div("input-group-text") {
                            span("fas fa-lock") {}
                        }
                    }
                }
                div("input-group mb-3") {
                    div("text-center m-auto") {
                        AsyncHCaptcha::class {
                            ref = asyncHCaptchaRef
                            siteKeyLoader = {
                                StoreBff.loadHCaptchaKey()
                            }
                            onVerify = { token, _ ->
                                setHCaptchaToken(token)
                            }
                            if (document.body.className.contains("dark-mode")) {
                                theme = "dark"
                            }
                        }
                    }
                }
                div("row") {
                    div("col-8") {
                        div("icheck-primary") {
                            input {
                                type = InputType.checkbox
                                ref = rememberMeRef
                                id = "remember"
                                checked = persistent ?: authCtx.persist
                                onChange = { ev ->
                                    setPersistent(ev.target.checked)
                                }
                            }
                            label {
                                htmlFor = "remember"
                                +"Lembrar-se de mim"
                            }
                        }
                    }

                    div("col-4") {
                        button("btn btn-primary btn-block") {
                            type = ButtonType.submit
                            ref = submitRef
                            disabled = !readyToSubmit
                            +"Entrar"
                        }
                    }
                }
            }
            /*div("social-auth-links text-center mt-2 mb-3") {
                a("btn btn-block btn-discord") {
                    href = "#"
                    i("fab fa-discord mr-2") {}
                    +"Entrar usando o Discord"
                }
            }*/
            div {
                button("btn btn-outline-secondary btn-block mt-3") {
                    +"Esqueci minha senha"
                    onClick = {
                        logEvent(
                            category = GACategory.LOGIN,
                            action = GAAction.FORGOT_PASSWORD,
                            label = "Clicou em \"Esqueci minha senha\""
                        )
                        alert("Desculpe, esta função ainda não foi implementada. Se precisa de ajudar por favor entre em contato conosco pelo Discord. O link pode ser encontrado no menu superior da página inicial.")
                    }
                }
                Link("btn btn-outline-primary btn-block mt-2") {
                    to = PublicSitemap.REGISTER.path
                    state = jso<RegisterLocationState> {
                        returnLocation = location.pathname
                        returnState = location.state
                    }
                    +"Cadastrar uma senha"
                }
            }
        }
    }
}
